import React, { useMemo } from 'react';

interface Club {
  id: number;
  type: string;
  clubNumber?: number;
  loft?: number;
  performance?: {
    averageDistance?: number;
  };
}

interface ClubDistanceChartProps {
  clubs: Club[];
}

export const ClubDistanceChart: React.FC<ClubDistanceChartProps> = ({ clubs }) => {
  // Filter clubs with distance data and sort them by expected distance
  const clubsWithData = useMemo(() => {
    return clubs
      .filter(club => club.performance?.averageDistance !== undefined && club.performance.averageDistance > 0)
      .sort((a, b) => {
        // Sort by expected distance (descending)
        const distanceA = a.performance?.averageDistance || 0;
        const distanceB = b.performance?.averageDistance || 0;
        return distanceB - distanceA;
      });
  }, [clubs]);

  // Get the maximum distance for scaling
  const maxDistance = useMemo(() => {
    if (clubsWithData.length === 0) return 300; // Default max if no data
    const max = Math.max(...clubsWithData.map(club => club.performance?.averageDistance || 0));
    return Math.ceil(max / 50) * 50; // Round up to nearest 50
  }, [clubsWithData]);

  // Format club label
  const getClubLabel = (club: Club) => {
    if (club.type === 'driver') return 'Dr';
    if (club.type === 'wood') return `${club.clubNumber}w`;
    if (club.type === 'hybrid') return `${club.clubNumber}h`;
    if (club.type === 'iron') return `${club.clubNumber}i`;
    if (club.type === 'wedge' && club.loft) return `${club.loft}°`;
    if (club.type === 'wedge') return 'SW';
    if (club.type === 'putter') return 'Pt';
    return '';
  };

  if (clubsWithData.length === 0 && false) {
    return (
      <div className="p-4 bg-secondary rounded-lg text-center">
        <p>Aucune donnée de distance disponible</p>
        <p className="text-sm text-gray-400 mt-1">
          Utilisez la fonction "Trouvez vos distances" pour enregistrer vos performances
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <h2 className="text-lg font-semibold mb-4">Moyenne des distances</h2>

      <div className="space-y-3">
        {clubsWithData.map(club => {
          const distance = club.performance?.averageDistance || 0;
          const percentage = (distance / maxDistance) * 100;

          return (
            <div key={club.id} className="flex items-center">
              <div className="w-10 flex-shrink-0">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium">{getClubLabel(club)}</span>
                </div>
              </div>

              <div className="flex-grow ml-2">
                <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>

              <div className="ml-3 w-12 text-right">
                <span className="font-bold">{distance}</span>
                <span className="text-xs text-gray-500 ml-1">m</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* <p className="text-xs text-gray-500 mt-4 text-center">
        Seuls les coups effectués via "Trouvez vos distances" sont affichés ici
      </p> */}
    </div>
  );
};
